import { FC, useEffect, useMemo, useRef, useState } from "react";

// Defaults builders
import { BuildVariableOperationsGpsBonus, BuildVariableOperationsGpsBonusSetup } from "@/PayPlans/helpers/DefaultSections";

// Utils
import {
	DEFAULT_MAXIMUM_COLUMNS,
	DEFAULT_MAXIMUM_TIERS,
	DEFAULT_MINIMUM_COLUMNS,
	DEFAULT_MINIMUM_TIERS,
	HIGHEST_NUMBER,
	PERCENT_MIN,
} from "./constants";
import { updateHeaderColumnInput, updateSideRowInput } from "./utils";

// Hooks
import { useValidations } from "@/Common/hooks/Validations";

// MUI
import { Box, Grid } from "@mui/material";

import { cloneDeep } from "lodash";
import MatrixTierColumns from "./MatrixTierColumns";
import MatrixTiers from "./MatrixTiers";
import { IBonus, IBonusTier } from "./types";

interface CommissionMatrixProps {
	testId?: string;
	tiers?: IBonusTier[];
	position?: string;
	readOnly?: boolean;
	minimumColumns?: number;
	maximumColumns?: number;
	minimumTiers?: number;
	maximumTiers?: number;
	columnTitle?: string;
	columnTitleTooltip?: string;
	columnLabel?: string;
	rowTitle?: string;
	rowTitleTooltip?: string;
	disableValidation?: boolean;
	evaluationType?: string;
	onActionUpdate?: (tiers: IBonusTier[]) => void;
	onActionValidation?: (isValid: boolean) => void;
	onEvaluationTypeUpdate?: (evaluationType: string) => void;
}

const CommissionMatrix: FC<CommissionMatrixProps> = (props) => {
	const {
		tiers = [],
		position = "",
		readOnly = false,
		minimumColumns = DEFAULT_MINIMUM_COLUMNS,
		maximumColumns = DEFAULT_MAXIMUM_COLUMNS,
		minimumTiers = DEFAULT_MINIMUM_TIERS,
		maximumTiers = DEFAULT_MAXIMUM_TIERS,
		rowTitle,
		rowTitleTooltip,
		columnTitle = "",
		columnTitleTooltip,
		columnLabel,
		disableValidation = false,
		evaluationType: propEvaluationType = "ACTIVATION",
		onActionUpdate,
		onActionValidation,
		onEvaluationTypeUpdate,
	} = props;

	const [innerModel, setInnerModel] = useState<IBonusTier[]>([]);
	const [evaluationType, setEvaluationType] = useState<string>(propEvaluationType);
	const { errors, isValid, getSectionsErrorMessages, validate } = useValidations();
	const propagateChangesRef = useRef(false);
	const forceValidationRef = useRef(false);

	const errorMessages = getSectionsErrorMessages(["columnInputErrors", "rowInputErrors", "bonusInputErrors", "minimumTiersErrors"]);
	const columnInputErrors = errors?.get("columnInputErrors");
	const rowInputErrors = errors?.get("rowInputErrors");
	const bonusInputErrors = errors?.get("bonusInputErrors");
	const minimumTiersErrors = errors?.get("minimumTiersErrors");

	const rowThreshold = Math.max(2, minimumTiers);
	const colThreshold = Math.max(2, minimumColumns);

	const disableAddColumn = useMemo(() => {
		return innerModel?.[0]?.bonuses?.length >= maximumColumns || readOnly;
	}, [innerModel, maximumColumns, readOnly]);

	const hasMinimumTiers = (tiers: IBonusTier[]) => {
		const columns = tiers?.[0]?.bonuses?.length || 0;
		const rows = tiers.length;
		return columns >= colThreshold || rows >= rowThreshold;
	};

	const enforceValidationAndPropagate = (updatedModel: IBonusTier[]) => {
		const meetsMinimum = hasMinimumTiers(updatedModel);
		propagateChangesRef.current = meetsMinimum;
		forceValidationRef.current = !meetsMinimum;
	};

	const handleAddColumn = () => {
		const updatedModel = [...innerModel];
		const bonuses = updatedModel[0]?.bonuses || [];
		const [bonus] = bonuses;
		const columnInputType = bonus?.from?.input;
		const lastIndex = bonuses.length - 1;

		updatedModel.forEach((tier) => {
			tier.bonuses.push(
				BuildVariableOperationsGpsBonus({
					from: PERCENT_MIN,
					fromInputType: columnInputType,
					to: -HIGHEST_NUMBER,
					toInputType: columnInputType,
					bonus: 0,
					bonusInputType: bonus?.bonus?.input,
				})
			);
			tier.bonuses[lastIndex].to.value = 0;
		});

		forceValidationRef.current = true;
		setInnerModel(updatedModel);
	};

	const handleDeleteColumn = () => {
		const updatedModel = [...innerModel];
		updatedModel.forEach((tier) => {
			tier.bonuses.splice(-1);
			const lastIndex = tier.bonuses.length - 1;
			tier.bonuses[lastIndex].to.value = -HIGHEST_NUMBER;
		});

		enforceValidationAndPropagate(updatedModel);
		setInnerModel(updatedModel);
	};

	const handleAddTier = () => {
		const updatedModel = [...innerModel];
		const bonuses = updatedModel[0]?.bonuses || [];
		const [bonus] = bonuses;
		const columnInputType = bonus?.from?.input;
		const rowInputType = updatedModel[0]?.from?.input;
		const bonusInputType = bonus?.bonus?.input;
		const lastIndex = updatedModel.length - 1;

		const { combined } = BuildVariableOperationsGpsBonusSetup({
			columnInputType,
			rowInputType,
			bonusInputType,
			fromTier: 0,
			defaultColumns: bonuses.length,
		});

		updatedModel.push(combined.tiers[0]);
		updatedModel[lastIndex].to.value = 0;

		forceValidationRef.current = true;
		setInnerModel(updatedModel);
	};

	const handleDeleteTier = () => {
		const updatedModel = [...innerModel];
		updatedModel.splice(-1);
		const lastIndex = updatedModel.length - 1;
		updatedModel[lastIndex].to.value = -HIGHEST_NUMBER;

		enforceValidationAndPropagate(updatedModel);
		setInnerModel(updatedModel);
	};

	const handleUpdateInputType = (...params: any[]) => {
		const [, , location, area, input] = params;
		const updatedModel = [...innerModel];

		switch (location) {
			case "column": {
				updatedModel.forEach((tier: IBonusTier) => {
					tier.bonuses?.forEach((bonus: IBonus, bonusIndex: number) => {
						bonus.to.input = input;
						bonus.from.input = input;
						const currentValue = area === "from" ? bonus.from.value : bonus.to.value;
						if (currentValue !== undefined) {
							updateHeaderColumnInput({
								updatedModel,
								column: bonusIndex,
								updatedValue: 0,
								area,
								position,
								inputType: input,
								reset: true,
							});
						}
					});
				});
				break;
			}
			case "row":
				if (area === "bonus") {
					updatedModel.forEach((tier: IBonusTier) => {
						tier.bonuses?.forEach((bonus: IBonus) => {
							bonus.bonus.input = input;
							bonus.bonus.value = 0;
						});
					});
				} else {
					updatedModel.forEach((tier: IBonusTier, tierIndex: number) => {
						tier.to.input = input;
						tier.from.input = input;
						const currentRowValue = area === "from" ? tier.from.value : tier.to.value;
						if (currentRowValue !== undefined) {
							updateSideRowInput({
								updatedModel,
								row: tierIndex,
								column: 0,
								updatedValue: 0,
								position,
								area,
								inputType: input,
								reset: true,
							});
						}
					});
				}
				break;
			default:
				break;
		}

		propagateChangesRef.current = isValid;
		setInnerModel(updatedModel);
	};

	const handleUpdateValue = (...params: any[]) => {
		const [row, column, location, area, value] = params;
		const updatedModel = [...innerModel];
		const updatedValue = value ? Number(value) : 0;

		switch (location) {
			case "column": {
				const inputType = updatedModel[row]?.bonuses?.[column]?.from?.input;
				updateHeaderColumnInput({ updatedModel, column, updatedValue, area, position, inputType });
				break;
			}
			case "row": {
				const inputType = updatedModel[row]?.from?.input;
				updateSideRowInput({ updatedModel, row, column, updatedValue, position, area, inputType });
				break;
			}
			default:
				break;
		}

		propagateChangesRef.current = true;
		setInnerModel(updatedModel);
	};

	const handleEvaluationTypeChange = (evaluationType: string) => {
		setEvaluationType(evaluationType);
		propagateChangesRef.current = true;
	};

	useEffect(() => {
		if (innerModel?.length) {
			const shouldValidate = !disableValidation || forceValidationRef.current;
			const columnBonuses = shouldValidate ? innerModel?.[0]?.bonuses || [] : [];
			const rowData = shouldValidate ? innerModel : [];

			validate?.([
				{
					definition: {
						useKey: "columnInputErrors",
						operators: {
							conditions: [
								{
									type: "INLINE",
									on: "value",
									compare: ">",
								},
								{
									type: "STEP",
									on: "value",
									compare: "===",
									deltas: { Dollar: 0, Percent: 0, Unit: 0 },
									distinct: 0,
								},
							],
						},
						errorsMap: ["from", "to"],
						message: `${columnTitle} must be in descending order.`,
						validationType: "DOUBLE_INPUT",
						specialProperties: {
							noFirstError: true,
							noLastError: true,
						},
					},
					section: columnBonuses,
				},
				{
					definition: {
						useKey: "rowInputErrors",
						operators: {
							conditions: [
								{
									type: "INLINE",
									on: "value",
									compare: ">",
								},
								{
									type: "STEP",
									on: "value",
									compare: "===",
									deltas: { Dollar: 0, Percent: 0, Unit: 0 },
									distinct: 0,
								},
							],
						},
						errorsMap: ["from", "to"],
						message: `${rowTitle} must be in descending order.`,
						validationType: "DOUBLE_INPUT",
						specialProperties: {
							noFirstError: true,
							noLastError: true,
						},
					},
					section: rowData,
				},
				{
					definition: {
						useKey: "bonusInputErrors",
						operators: {
							conditions: [
								{
									order: "ROWS",
									type: "STEP",
									on: "value",
									compare: "<=",
								},
								{
									order: "COLUMNS",
									type: "STEP",
									on: "value",
									compare: "<=",
									deltas: { Dollar: 0, Percent: 0 },
								},
								{
									order: "DIAGONAL",
									on: "value",
									compare: "<",
								},
							],
						},
						errorsMap: ["bonus"],
						errorsMapOn: "bonuses",
						message: "Bonus amounts must be in ascending order.",
						validationType: "MATRIX",
					},
					section: rowData,
				},
				{
					definition: {
						useKey: "minimumTiersErrors",
						validationType: "CUSTOM",
						errorsMap: ["tiers"],
						message: `Please add at least ${rowThreshold} tiers or ${colThreshold} columns.`,
						operators: {
							customFn: (params: { tiers: IBonusTier[] }) => {
								const meetsMinimum = hasMinimumTiers(params.tiers);
								return [{ tiers: !meetsMinimum }];
							},
							params: { tiers: shouldValidate ? innerModel : [] },
						},
					},
					section: shouldValidate ? [{ tiers: false }] : [],
				},
			]);
		}
	}, [innerModel, disableValidation]);

	useEffect(() => {
		propagateChangesRef.current = false;
		forceValidationRef.current = false;
		setInnerModel(cloneDeep(tiers));
		setEvaluationType(propEvaluationType);
	}, [tiers, propEvaluationType]);

	useEffect(() => {
		onActionValidation?.(isValid);
		if (isValid && propagateChangesRef.current) {
			onActionUpdate?.(innerModel);
			onEvaluationTypeUpdate?.(evaluationType);
			propagateChangesRef.current = false;
		}
	}, [errors, isValid, innerModel, evaluationType]);

	return (
		<Box sx={{ width: "100%", overflowX: "auto", pb: 2 }}>
			<Grid container item mt={2} xs={12}>
				<MatrixTierColumns
					title={columnTitle}
					titleTooltip={columnTitleTooltip}
					columnLabel={columnLabel}
					readOnly={readOnly}
					tiers={innerModel}
					minimumColumns={minimumColumns}
					errors={columnInputErrors}
					disableAddColumn={disableAddColumn}
					position={position}
					evaluationType={evaluationType}
					handleDeleteColumn={handleDeleteColumn}
					handleAddColumn={handleAddColumn}
					handleUpdateValue={handleUpdateValue}
					handleUpdateInputType={handleUpdateInputType}
					onEvaluationTypeChange={handleEvaluationTypeChange}
				/>
				<MatrixTiers
					title={rowTitle}
					titleTooltip={rowTitleTooltip}
					readOnly={readOnly}
					tiers={innerModel}
					disableAddTier={innerModel?.length >= maximumTiers || readOnly}
					disableDeleteTier={innerModel?.length <= minimumTiers || readOnly}
					handleDeleteTier={handleDeleteTier}
					handleAddTier={handleAddTier}
					minimumColumns={minimumColumns}
					rowInputErrors={rowInputErrors}
					bonusInputErrors={bonusInputErrors || minimumTiersErrors}
					handleUpdateValue={handleUpdateValue}
					handleUpdateInputType={handleUpdateInputType}
					errorMessages={errorMessages}
					position={position}
				/>
			</Grid>
		</Box>
	);
};

export default CommissionMatrix;
